import * as Sentry from '@sentry/nextjs'
import ky from 'ky'
import { isEmpty } from 'lodash-es'
import useSWR from 'swr'

import { useTelemetryCredentials } from '@/feature/telemetry/api/useMyTelemetryCredentials'
import { TELEMETRY_URL } from '@/feature/telemetry/util/constants'

import { AssetLocationHistory } from './useAssetLocationHistory'
import { fetchTripLocationTrail, TripLocationTrailQuery } from './useTripLocationTrail.utils'

// Stable, so a trail that has not loaded yet doesn't hand replay a fresh array — and a recomputed
// route — on every render.
const EMPTY_TRAIL: AssetLocationHistory = []

// Instants as ISO strings, so the key compares by value rather than by Date identity.
type TrailKey = readonly ['trip-location-trail', string, string, string, string, string, string]

const trailFetcher = async ([
  ,
  fleetName,
  tripId,
  start,
  end,
  maxResolution,
  token,
]: TrailKey) => {
  try {
    return await fetchTripLocationTrail({
      client: ky.create({
        prefixUrl: TELEMETRY_URL,
        headers: { Authorization: `Token ${token}` },
      }),
      fleetName,
      tripId,
      startInstant: new Date(start),
      endInstant: new Date(end),
      maxResolution,
    })
  } catch (error) {
    Sentry.captureException(error, {
      tags: { feature: 'replay', api: 'v2/api/fleets/:fleet-name/trips/location-trail' },
      extra: { fleetName, tripId, startInstant: start, endInstant: end, maxResolution },
    })
    throw error
  }
}

/**
 * A trip's stored location trail, for replaying a task (BRI-1588). Pass `null` as the trip to
 * hold off fetching until replay is actually opened.
 *
 * Changing the resolution refetches, and the previous trail stays on screen meanwhile so the map
 * is never blanked out by a slider drag.
 */
export const useTripLocationTrail = (trip: TripLocationTrailQuery | null, maxResolution: string) => {
  const { data: credentials } = useTelemetryCredentials()
  const fleetName = credentials?.telemetryFleetName
  const token = credentials?.telemetryToken

  const key: TrailKey | null =
    trip && fleetName && !isEmpty(token)
      ? [
          'trip-location-trail',
          fleetName,
          trip.tripId,
          trip.startInstant.toISOString(),
          trip.endInstant.toISOString(),
          maxResolution,
          token as string,
        ]
      : null

  const { data, error, isValidating } = useSWR<AssetLocationHistory, Error, TrailKey | null>(
    key,
    trailFetcher,
    {
      keepPreviousData: true,
      // A trail is history: it only grows while the trip runs, and a refetch is a full cold
      // storage query, so it is not worth repeating on every focus.
      revalidateOnFocus: false,
      shouldRetryOnError: false,
    },
  )

  return {
    data: data ?? EMPTY_TRAIL,
    error,
    // Nothing to draw yet — including while the credentials the request needs are still loading,
    // so a trip is never reported as having no trail before it has actually been asked for.
    isLoading: trip != null && data === undefined && error === undefined,
    isRefreshing: isValidating && data !== undefined,
  }
}
